import { Coins, CornerDownRight, Minus, Plus } from "lucide-react";
import { PaginationControls } from "@/bizminer/App/Credits/_components/PaginationControls.tsx";
import type { PagyMetadata } from "@/types";
import type { BizminerAppCreditsTransaction } from "@/types/serializers";

const categoryLabels: Record<string, string> = {
  manual_grant: "Manual Grant",
  bonus: "Bonus",
  referral_bonus: "Referral Bonus",
  subscription_refill: "Subscription Refill",
  credit_pack: "Credit Pack",
  analysis: "Analysis",
  operation: "Operation",
  refund: "Refund",
  admin_adjustment: "Admin Adjustment",
  expiration: "Expiration",
};
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface ReasonCellProps {
  transaction: BizminerAppCreditsTransaction;
  isRefundChild: boolean;
}

const ReasonCell = ({ transaction, isRefundChild }: ReasonCellProps) => {
  if (transaction.refunded && !isRefundChild) {
    return (
      <>
        {transaction.reason ?? "-"}
        <br />
        <span className="text-amber-600 text-xs font-medium">
          Refunded below ↓
        </span>
      </>
    );
  }
  return <>{transaction.reason ?? "-"}</>;
};

interface TransactionRowProps {
  transaction: BizminerAppCreditsTransaction;
  isRefundChild?: boolean;
}

const TransactionRow = ({
  transaction,
  isRefundChild = false,
}: TransactionRowProps) => {
  return (
    <>
      <tr
        className={`hover:bg-muted/30 transition-colors ${isRefundChild ? "bg-muted/20" : ""}`}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <div className="flex items-center gap-2">
            {isRefundChild && (
              <CornerDownRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <span>{formatDate(transaction.created_at)}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            {transaction.is_credit ? (
              <Plus className="h-4 w-4 text-chart-6" />
            ) : (
              <Minus className="h-4 w-4 text-destructive" />
            )}
            <span className="text-sm font-medium">
              {categoryLabels[transaction.category] || transaction.category}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <span
            className={`text-sm font-semibold ${
              transaction.is_credit ? "text-chart-6" : "text-destructive"
            }`}
          >
            {transaction.is_credit ? "+" : ""}
            {transaction.amount}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <span className="text-sm font-medium">
            {transaction.balance_after}
          </span>
        </td>
        <td className="px-6 py-4 text-sm text-muted-foreground">
          <ReasonCell transaction={transaction} isRefundChild={isRefundChild} />
        </td>
      </tr>

      {/* Render nested refund */}
      {transaction.refund && (
        <TransactionRow transaction={transaction.refund} isRefundChild={true} />
      )}
    </>
  );
};
const EmptyState = () => {
  return (
    <tr>
      <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <Coins className="h-12 w-12 opacity-50" />
          <p>No transactions yet</p>
        </div>
      </td>
    </tr>
  );
};

interface TransactionHistoryTableProps {
  transactions: BizminerAppCreditsTransaction[];
  pagy: PagyMetadata;
}

export const TransactionHistoryTable = ({
  transactions,
  pagy,
}: TransactionHistoryTableProps) => {
  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold">Transaction History</h2>
        <p className="text-sm text-muted-foreground mt-1">
          View all credit transactions and balance changes
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Balance After
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {transactions.length === 0 ? (
              <EmptyState />
            ) : (
              transactions.map((tx) => (
                <TransactionRow key={tx.id} transaction={tx} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {transactions.length > 0 && <PaginationControls pagy={pagy} />}
    </div>
  );
};
